<template>
  <section class="xpress-documentation-section">
    <article class="document-content">
      <section class="heading-container">
        <h1 class="caption">Batch Upload With Account Validation</h1>
        <p>
          This endpoint provide access to batch upload with account number to be validated.
        </p>
        <p><span class="fw-bold">Method: </span>POST</p>
        <p><span class="fw-bold">Header: </span>AccessToken</p>
        <p>
          <span class="fw-bold">Url: </span><span class="api-url">/api/v1/FundDisbursement/XPBulk</span>
        </p>
      </section>
      <!-- ------------------------- -->
      <section class="sub-heading-container">
        <h5 class="sub-caption">Sample Requests</h5>
      </section>
      <section class="xpress-doc-code-bg">
        <div class="request-response-container">
          <code class="main-brace">{</code>
          <code class="key-value-pair">
            <li>
              <span class="obj-key">"batchNumber"</span>:&nbsp;
              <span class="obj-value">"6843"</span>,
            </li>
            <li>
              <span class="obj-key">"debitAccount"</span>:&nbsp;
              <span class="obj-value">"0049648066"</span>,
            </li>
            <li>
              <span class="obj-key">"paymentTransactionChannelCode"</span>:&nbsp;
              <span class="obj-value">"002"</span>,
            </li>
            <!-- inner object value-------- -->
            <li>
              <span class="obj-key">"transactions"</span>:&nbsp;
              <span class="obj-value-brace">[</span>
            </li>
            <!-- inner object-------- -->
            <li>
              <div class="inner-key-value-pair">
                <span class="obj-value-brace">{</span>
              </div>
              <div class="inner-key-value-pair">
                <div class="inner-key-value-pair">
                  <span class="obj-key">"beneficiaryAccount"</span>:&nbsp;
                  <span class="obj-value">"3060222997"</span>,
                </div>
                <div class="inner-key-value-pair">
                  <span class="obj-key">"beneficiaryName"</span>:&nbsp;
                  <span class="obj-value">"owolabi olajumoke helen"</span>,
                </div>
                <div class="inner-key-value-pair">
                  <span class="obj-key">"beneficiaryBankCode"</span>:&nbsp;
                  <span class="obj-value">"011"</span>,
                </div>
                <div class="inner-key-value-pair">
                  <span class="obj-key">"transactionAmount"</span>:&nbsp;
                  <span class="obj-value num">10</span>,
                </div>
                <div class="inner-key-value-pair">
                  <span class="obj-key">"transactionReference"</span>:&nbsp;
                  <span class="obj-value">""</span>,
                </div>
                <div class="inner-key-value-pair">
                  <span class="obj-key">"transactionNumber"</span>:&nbsp;
                  <span class="obj-value">"51912"</span>,
                </div>
                <div class="inner-key-value-pair">
                  <span class="obj-key">"narration"</span>:&nbsp;
                  <span class="obj-value">"fee"</span>
                </div>
              </div>
              <div class="inner-key-value-pair">
                <span class="obj-value-brace">}</span>
              </div>
            </li>
            <!-- -------------------------- -->
            <li>
              <span class="obj-value-brace">]</span>
            </li>
          </code>
          <!-- -------------------------- -->
          <code class="main-brace">}</code>
        </div>
      </section>

      <section class="sub-heading-container">
        <h5 class="sub-caption">Sample Response</h5>
      </section>
      <!-- ------------------------- -->
      <section class="xpress-doc-code-bg">
        <div class="request-response-container">
          <code class="main-brace">{</code>
          <!-- ------ -->
          <code class="key-value-pair">
            <li>
              <span class="obj-key">"data"</span>:&nbsp;
              <span class="obj-value-brace">{</span>
            </li>
            <!-- inner object value -->
            <li>
              <div class="inner-key-value-pair">
                <span class="obj-key">"batchNumber"</span>:&nbsp;
                <span class="obj-value">"68439"</span>,
              </div>
            </li>
            <li>
              <div class="inner-key-value-pair">
                <span class="obj-key">"debitAccount"</span>:&nbsp;
                <span class="obj-value">"0049648066"</span>,
              </div>
            </li>
            <li>
              <div class="inner-key-value-pair">
                <span class="obj-key">"batchReference"</span>:&nbsp;
                <span class="obj-value">"hel16622373112021"</span>,
              </div>
            </li>
            <li>
              <div class="inner-key-value-pair">
                <span class="obj-key">"paymentTransactionChannelCode"</span>:&nbsp;
                <span class="obj-value">"002"</span>
              </div>
            </li>
            <!-- inner close brace -->
            <li>
              <span class="obj-value-brace">}</span>,
            </li>
            <li>
              <span class="obj-key">"responseCode"</span>:&nbsp;
              <span class="obj-value">"00"</span>,
            </li>
            <li>
              <span class="obj-key">"responseMessage"</span>:&nbsp;
              <span class="obj-value">"Batch Acknowledged"</span>
            </li>
          </code>
          <!-- ------ -->
          <code class="main-brace">}</code>
        </div>
      </section>
    </article>
    <!-- ---------------------------------------- -->
    <article class="document-content">
      <section class="heading-container">
        <h1 class="caption">Batch Upload Without Account Validation</h1>
        <p>
          This endpoint provide access to batch upload without the account number being validated.
        </p>
        <p><span class="fw-bold">Method: </span>POST</p>
        <p><span class="fw-bold">Header: </span>AccessToken</p>
        <p>
          <span class="fw-bold">Url: </span><span class="api-url">/api/v1/FundDisbursement/XPWBulk</span>
        </p>
      </section>
      <!-- ------------------------- -->
      <section class="sub-heading-container">
        <h5 class="sub-caption">Sample Requests</h5>
      </section>
      <section class="xpress-doc-code-bg">
        <div class="request-response-container">
          <code class="main-brace">{</code>
          <code class="key-value-pair">
            <li>
              <span class="obj-key">"batchNumber"</span>:&nbsp;
              <span class="obj-value">"6543"</span>,
            </li>
            <li>
              <span class="obj-key">"debitAccount"</span>:&nbsp;
              <span class="obj-value">"0049648066"</span>,
            </li>
            <li>
              <span class="obj-key">"paymentTransactionChannelCode"</span>:&nbsp;
              <span class="obj-value">"002"</span>,
            </li>
            <!-- inner object value-------- -->
            <li>
              <span class="obj-key">"transactions"</span>:&nbsp;
              <span class="obj-value-brace">[</span>
            </li>
            <!-- inner object-------- -->
            <li>
              <div class="inner-key-value-pair">
                <span class="obj-value-brace">{</span>
              </div>
              <div class="inner-key-value-pair">
                <div class="inner-key-value-pair">
                  <span class="obj-key">"beneficiaryAccount"</span>:&nbsp;
                  <span class="obj-value">"3060222997"</span>,
                </div>
                <div class="inner-key-value-pair">
                  <span class="obj-key">"beneficiaryName"</span>:&nbsp;
                  <span class="obj-value">"owolabi olajumoke helen"</span>,
                </div>
                <div class="inner-key-value-pair">
                  <span class="obj-key">"beneficiaryBankCode"</span>:&nbsp;
                  <span class="obj-value">"011"</span>,
                </div>
                <div class="inner-key-value-pair">
                  <span class="obj-key">"transactionAmount"</span>:&nbsp;
                  <span class="obj-value num">10</span>,
                </div>
                <div class="inner-key-value-pair">
                  <span class="obj-key">"transactionReference"</span>:&nbsp;
                  <span class="obj-value">""</span>,
                </div>
                <div class="inner-key-value-pair">
                  <span class="obj-key">"transactionNumber"</span>:&nbsp;
                  <span class="obj-value">"677"</span>,
                </div>
                <div class="inner-key-value-pair">
                  <span class="obj-key">"narration"</span>:&nbsp;
                  <span class="obj-value">"fee"</span>
                </div>
              </div>
              <div class="inner-key-value-pair">
                <span class="obj-value-brace">}</span>
              </div>
            </li>
            <!-- -------------------------- -->
            <li>
              <span class="obj-value-brace">]</span>
            </li>
          </code>
          <!-- -------------------------- -->
          <code class="main-brace">}</code>
        </div>
      </section>

      <section class="sub-heading-container">
        <h5 class="sub-caption">Sample Response</h5>
      </section>
      <!-- ------------------------- -->
      <section class="xpress-doc-code-bg">
        <div class="request-response-container">
          <code class="main-brace">{</code>
          <!-- ------ -->
          <code class="key-value-pair">
            <li>
              <span class="obj-key">"data"</span>:&nbsp;
              <span class="obj-value-brace">{</span>
            </li>
            <!-- inner object value -->
            <li>
              <div class="inner-key-value-pair">
                <span class="obj-key">"batchNumber"</span>:&nbsp;
                <span class="obj-value">"65563"</span>,
              </div>
            </li>
            <li>
              <div class="inner-key-value-pair">
                <span class="obj-key">"debitAccount"</span>:&nbsp;
                <span class="obj-value">"0049648066"</span>,
              </div>
            </li>
            <li>
              <div class="inner-key-value-pair">
                <span class="obj-key">"batchReference"</span>:&nbsp;
                <span class="obj-value">"hel37807533112021"</span>,
              </div>
            </li>
            <li>
              <div class="inner-key-value-pair">
                <span class="obj-key">"paymentTransactionChannelCode"</span>:&nbsp;
                <span class="obj-value">"002"</span>
              </div>
            </li>
            <!-- inner close brace -->
            <li>
              <span class="obj-value-brace">}</span>,
            </li>
            <li>
              <span class="obj-key">"responseCode"</span>:&nbsp;
              <span class="obj-value">"00"</span>,
            </li>
            <li>
              <span class="obj-key">"responseMessage"</span>:&nbsp;
              <span class="obj-value">"Batch Acknowledged"</span>
            </li>
          </code>
          <!-- ------ -->
          <code class="main-brace">}</code>
        </div>
      </section>
    </article>
  </section>
</template>

<script>
export default {
  name: 'Batch Uploads'
}
</script>
